import React from 'react';

/**
 * Ghost Flag Background — a translucent American flag with configurable color
 * theme. "Ghost" means low opacity so text stays perfectly readable, but the
 * palette can range from the classic red/white/blue to fully tone-on-tone.
 *
 *   classic     — red / white / blue (default)
 *   navy_mono   — tone-on-tone navy (previous default)
 *   silver      — silver stripes only, no color
 *   gold_bronze — warm gold + bronze stripes
 *
 * Intensity presets adjust the overall opacity so "Subtle" is barely visible,
 * "Bold" is unmistakable but never distracting.
 */

const INTENSITY_OPACITY = {
  subtle: 0.08,
  medium: 0.15,
  bold:   0.24,
};

const PALETTES = {
  classic: {
    stripeAlt: '#b91c1c',      // red-700
    stripeBase: '#f8fafc',      // near-white
    canton: '#1e3a8a',          // blue-900
    star: '#f8fafc',
  },
  navy_mono: {
    stripeAlt: 'rgba(148,163,184,0.28)',
    stripeBase: 'transparent',
    canton: 'rgba(30,41,82,0.9)',
    star: 'rgba(255,255,255,0.55)',
  },
  silver: {
    stripeAlt: 'rgba(203,213,225,0.55)',
    stripeBase: 'transparent',
    canton: 'rgba(51,65,85,0.7)',
    star: 'rgba(248,250,252,0.75)',
  },
  gold_bronze: {
    stripeAlt: '#b45309',       // amber-800
    stripeBase: '#fef3c7',       // amber-100
    canton: '#78350f',           // amber-900
    star: '#fef3c7',
  },
};

const GhostFlagBackground = ({ intensity = 'subtle', theme = 'classic' }) => {
  const opacity = INTENSITY_OPACITY[intensity] ?? INTENSITY_OPACITY.subtle;
  const p = PALETTES[theme] || PALETTES.classic;
  const stripeH = 1000 / 13;
  const cantonH = stripeH * 7;
  const cantonW = 760;

  return (
    <div className="fixed inset-0 pointer-events-none -z-10" aria-hidden="true" data-testid="ghost-flag-bg" data-theme={theme}>
      <svg viewBox="0 0 1900 1000" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full" style={{ opacity }}>
        {/* 13 stripes */}
        {Array.from({ length: 13 }).map((_, i) => (
          <rect key={i} x="0" y={i * stripeH} width="1900" height={stripeH} fill={i % 2 === 0 ? p.stripeBase : p.stripeAlt} />
        ))}
        {/* Canton */}
        <rect x="0" y="0" width={cantonW} height={cantonH} fill={p.canton} />
        {/* 50 stars in 9-row alternating pattern (6-5-6-5-6-5-6-5-6) */}
        <g fill={p.star}>
          {Array.from({ length: 9 }).map((_, row) => {
            const isEven = row % 2 === 0;
            const count = isEven ? 6 : 5;
            const yStep = cantonH / 10;
            const y = (row + 1) * yStep;
            const xOffset = isEven ? 70 : 130;
            return Array.from({ length: count }).map((__, col) => (
              <Star key={`${row}-${col}`} cx={xOffset + col * 115} cy={y} size={11} />
            ));
          })}
        </g>
      </svg>
    </div>
  );
};

const Star = ({ cx, cy, size }) => {
  const pts = [];
  for (let i = 0; i < 10; i += 1) {
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? size : size / 2.5;
    pts.push(`${cx + Math.cos(angle) * r},${cy + Math.sin(angle) * r}`);
  }
  return <polygon points={pts.join(' ')} />;
};

export default GhostFlagBackground;
